import {
    Component,
    computed,
    effect,
    Inject,
    inject,
    signal,
    untracked,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogActions } from '@angular/material/dialog';
import { ToastService } from '@app/core/services/toast.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeliveryNotesService } from 'api/services';
import { DeliveryNotesV3Service } from 'api-v3/services';
import { DeliveryNoteInPoolV3, SortType } from 'api-v3/models';
import { lastValueFrom } from 'rxjs';
import { FolderData } from '@app/shared/models/table-data-types';
import { DeliveryNoteData } from '@app/shared/models/store-types';
import { SearchStore } from '@app/stores/searchStore';
import { SelectionStore } from '@app/stores/selectionStore';
import { GenericTableComponent } from '@app/shared/components/generic-table/generic-table.component';
import { ColumnCreator } from '@app/shared/services/view-configs/column-creator';
import {
    SortEvent,
    TableColumn,
    TableConfiguration,
} from '@app/shared/models/generic-table.interface';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

type PoolSortBy = Exclude<
    Parameters<DeliveryNotesV3Service['getDeliveryNotesMetadataInPoolV3']>[0],
    undefined
>['sortBy'];

/** Chunk size per request while collecting the whole pool. */
const FETCH_PAGE_SIZE = 200;
/** Safety bound so a bad `hasMore` can never loop forever. */
const FETCH_MAX_NOTES = 2000;

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-add-notes-to-bundle-dialog',
    templateUrl: './add-notes-to-bundle-dialog.component.html',
    styleUrls: ['./add-notes-to-bundle-dialog.component.scss'],
    // Scoped store instances so the embedded generic table's pagination and
    // selection state don't clobber the view behind the dialog.
    providers: [SearchStore, SelectionStore],
    imports: [
        MatDialogTitle,
        MatIconButton,
        MatDialogClose,
        MatIcon,
        GenericTableComponent,
        MatDialogActions,
        MatButton,
        TranslateModule,
    ],
})
export class AddNotesToBundleDialogComponent {
    protected readonly searchStore = inject(SearchStore);
    protected readonly selectionStore = inject(SelectionStore);

    /** Every pool note that is not part of a bundle yet, unpaginated. */
    private readonly unbundledNotes = signal<DeliveryNoteData[]>([]);

    // The endpoint cannot filter out bundled notes, so the whole pool is
    // fetched, filtered here, and paginated client-side.
    tableData = computed(() => {
        const { offset, limit } = this.searchStore.pagination();
        return this.unbundledNotes().slice(offset, offset + limit);
    });

    tableLoading = signal(false);
    loading = signal(false);

    organizationSiteKey: string;

    /** `sortBy:sortDirection` of the data currently held, or null if none. */
    private lastFetchedSort: string | null = null;

    columns: TableColumn[] = [
        ColumnCreator.select({
            selectableCondition: item =>
                !(item as DeliveryNoteData).deliveryNoteBundleKey,
        }),
        ColumnCreator.deliveryNoteNumber(),
        ColumnCreator.orderNumber(),
    ] as TableColumn[];

    tableConfig: TableConfiguration = {
        columns: this.columns,
        features: {
            sortable: true,
            paginated: true,
            pagination: true,
            selectable: true,
        },
        paginationOptions: [10, 20, 50],
        autoReload: { enabled: false },
        trackBy: (_index, item) => (item as DeliveryNoteData)._key,
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private data: {
            deliveryNoteBundle: FolderData;
            organizationSiteKey: string;
        },
        private deliveryNotesService: DeliveryNotesService,
        private deliveryNotesV3Service: DeliveryNotesV3Service,
        private dialogRef: MatDialogRef<AddNotesToBundleDialogComponent>,
        private toastr: ToastService,
        private translateService: TranslateService
    ) {
        this.organizationSiteKey = data.organizationSiteKey;

        // Initial load plus reload whenever the (dialog-scoped) sort state
        // triggers a refresh. Pagination is served from the already fetched
        // list, so page changes must not hit the API again.
        effect(() => {
            this.searchStore.refreshTrigger();
            untracked(() => {
                const { sortBy, sortDirection } = this.searchStore.sort();
                if (`${sortBy}:${sortDirection}` === this.lastFetchedSort) {
                    // tableData() re-slices on its own, only the paginator
                    // bounds need to follow the new offset/limit.
                    this.syncPagination();
                    return;
                }
                void this.getTableData();
            });
        });
    }

    onSortChange(event: SortEvent): void {
        if (!event.direction) {
            return;
        }
        this.searchStore.updateSort(
            event.key as Parameters<typeof this.searchStore.updateSort>[0],
            event.direction
        );
        this.searchStore.triggerDataRefresh();
    }

    async addNotesToBundle(): Promise<void> {
        // The dialog-scoped SelectionStore only ever holds rows of this table,
        // so every selected item is a delivery note.
        const notes = (
            this.selectionStore.selectedItems() as DeliveryNoteData[]
        ).filter(note => !note.deliveryNoteBundleKey);

        if (notes.length === 0) {
            return;
        }
        this.loading.set(true);

        try {
            await lastValueFrom(
                this.deliveryNotesService.postExistingDeliveryNoteIntoExistingBundle(
                    {
                        organizationSiteKey: this.organizationSiteKey,
                        deliveryNoteBundleKey:
                            this.data.deliveryNoteBundle._key,
                        body: notes.map(n => n._key),
                    }
                )
            );
            this.loading.set(false);
            this.toastr.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.SUCCESS'
                )
            );
            this.dialogRef.close(notes);
        } catch {
            this.loading.set(false);
            this.toastr.error(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.ERROR'
                )
            );
            this.dialogRef.close([]);
        }
    }

    async getTableData(): Promise<void> {
        this.tableLoading.set(true);
        try {
            const sort = this.searchStore.sort();

            const notes = await this.fetchAllPoolNotes(
                sort.sortDirection === 'asc' ? SortType.Asc : SortType.Desc,
                (sort.sortBy ?? undefined) as PoolSortBy
            );

            this.unbundledNotes.set(
                notes
                    // Notes without a bundle carry an empty key, so a falsy
                    // check is the right test here.
                    .filter(note => !note.deliveryNoteBundleKey)
                    .map(note => ({
                        ...note,
                        orgSiteKey: this.organizationSiteKey,
                        read_only: false,
                    }))
            );
            this.lastFetchedSort = `${sort.sortBy}:${sort.sortDirection}`;
            this.syncPagination();
        } catch {
            this.toastr.error(
                this.translateService.instant(
                    'OUTGOING-GOODS.ADD-TO-BUNDLE-DIALOG.FETCHING-ERROR'
                )
            );
        } finally {
            this.tableLoading.set(false);
        }
    }

    /**
     * Collects the complete pool by walking the endpoint's own pagination.
     * Requesting one huge page instead would silently lose notes if the
     * backend caps `limit`, so the server's offsets are followed.
     */
    private async fetchAllPoolNotes(
        sort: SortType,
        sortBy: PoolSortBy
    ): Promise<DeliveryNoteInPoolV3[]> {
        const notes: DeliveryNoteInPoolV3[] = [];
        let offset = 0;
        let hasMore = false;

        do {
            const response = await lastValueFrom(
                this.deliveryNotesV3Service.getDeliveryNotesMetadataInPoolV3({
                    organizationSiteKey: this.organizationSiteKey,
                    limit: FETCH_PAGE_SIZE,
                    offset,
                    sort,
                    sortBy,
                })
            );

            const page = response?.data ?? [];
            if (page.length === 0) {
                break;
            }
            notes.push(...page);

            const paginationInfo = response?.paginationInfo;
            hasMore = paginationInfo?.hasMore ?? false;

            const nextOffset =
                paginationInfo?.nextOffset ?? offset + page.length;
            if (nextOffset <= offset) {
                // Never re-read a page: duplicate keys would break trackBy.
                break;
            }
            offset = nextOffset;
        } while (hasMore && notes.length < FETCH_MAX_NOTES);

        return notes;
    }

    /**
     * Points the paginator at the filtered list: the API's totalCount counts
     * bundled notes too, so it can't be used here.
     */
    private syncPagination(): void {
        const { offset, limit } = this.searchStore.pagination();
        const totalCount = this.unbundledNotes().length;

        this.searchStore.updatePagination({
            totalCount,
            hasMore: offset + limit < totalCount,
        });
    }
}
